/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { DoubleExpandCaret, ExpandCaret } from '@/Client/Icons';
import { COLORS, FONT } from '@/Client/Partials/CloseoutReviewFlow/closeoutTokens';
import { formatMonthDayTime } from '@/Client/Presenters';
import { styleguideFontSize } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { usePhotoUriForLayoutSize } from '@kaiizen/kai-pro-api-react-state';
import type { ScanPhoto } from '@kaiizen/kai-scan-template-system';

import { useProgressAlt } from './progressAltContext';
import {
  GAP_PX,
  PHOTO_VIEW_FILMSTRIP_HEIGHT_PX,
  PHOTO_VIEW_THUMBNAIL_WIDTH_PX,
  PHOTO_VIEW_TIME_NAV_WIDTH_PX,
  TILE_RADIUS_PX,
} from './progressAltMetrics';
import { getFirstPeriodStart, getRowLabel } from './progressAltRowLabelLib';
import { FOCUS_RING_STYLE } from './progressAltStyles';

const ROW_STYLE = css`
  label: PROGRESS_ALT_PHOTO_FILMSTRIP_STYLE;
  flex: 0 0 ${PHOTO_VIEW_FILMSTRIP_HEIGHT_PX}px;
  min-width: 0;
  display: flex;
  align-items: stretch;
  border-top: 1px solid ${COLORS.stroke};
  background: ${COLORS.appBg};
`;

/**
 * The time controls keep the date column's place at the left edge, so moving through the timeline is
 * the same gesture in both modes: the reader looks left for when, and along for what.
 *
 * Read top to bottom it is a stretch of the timeline itself — the bucket before, the one on screen,
 * the one after — which is why the buttons name where they go rather than merely which way.
 */
const TIME_NAV_STYLE = css`
  box-sizing: border-box;
  flex: 0 0 ${PHOTO_VIEW_TIME_NAV_WIDTH_PX}px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 2px;
  padding: 8px;
  border-right: 1px solid ${COLORS.stroke};
  background: ${COLORS.sunken};
`;

/** One height for every slot, so the bucket on screen stays dead centre however few neighbours it has. */
const TIME_SLOT_HEIGHT_PX = 20;

/**
 * Distance from the bucket on screen decides the size, and size alone — a second signal would say the
 * same thing twice, and dimming a legible date makes it read as unavailable. Straight off the styleguide
 * scale rather than a ramp of our own.
 */
const TIME_SLOT_FONT_SIZE_PX: Record<number, number> = {
  0: styleguideFontSize.displayAddress,
  1: styleguideFontSize.monoStageNum,
  2: styleguideFontSize.monoMicro,
};

/**
 * Ranged left, all five lines together. Centring each one would set five different indents down a
 * column of dates that vary in length and size — a ragged left edge the eye has to re-find on every
 * line. One left edge reads as a list, and matches the date column the stack stands in for.
 */
const TIME_SLOT_STYLE = css`
  box-sizing: border-box;
  flex: 0 0 ${TIME_SLOT_HEIGHT_PX}px;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 5px;
  padding: 0 4px 0 6px;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: ${COLORS.ink};
  font-family: ${FONT.mono};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: left;
`;

const TIME_STEP_STYLE = css`
  cursor: pointer;

  &:hover {
    background: ${COLORS.white};
  }

  ${FOCUS_RING_STYLE}
`;

/**
 * An empty slot still takes its height. Removing it outright would slide the bucket on screen off centre
 * and shift the whole stack every time the reader stepped near an end of the timeline.
 */
const TIME_SLOT_EMPTY_STYLE = css`
  visibility: hidden;
`;

const CARET_STYLE = css`
  flex: 0 0 auto;
  display: inline-flex;
`;

const TIME_NAV_TEXT_STYLE = css`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** The bucket on screen: the largest line in the stack, and the only one in the display face. */
const TIME_CURRENT_STYLE = css`
  font-family: ${FONT.display};
  letter-spacing: 0.01em;
  text-transform: none;
`;

const STRIP_STYLE = css`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${GAP_PX}px;
  padding: 0 16px;
  overflow-x: auto;
  overflow-y: hidden;
`;

const THUMBNAIL_STYLE = css`
  box-sizing: border-box;
  flex: 0 0 ${PHOTO_VIEW_THUMBNAIL_WIDTH_PX}px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;

  ${FOCUS_RING_STYLE}
`;

const THUMBNAIL_FRAME_STYLE = css`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border: 1px solid ${COLORS.stroke};
  border-radius: ${TILE_RADIUS_PX}px;
  background: ${COLORS.surfaceMuted};

  button:hover & {
    border-color: ${COLORS.midtone};
  }
`;

/** Inked rather than tinted: at this size a colour wash reads as part of the photo, a frame does not. */
const THUMBNAIL_FRAME_ACTIVE_STYLE = css`
  border-color: ${COLORS.ink};
  box-shadow: 0 0 0 2px ${COLORS.ink};
`;

const THUMBNAIL_IMAGE_STYLE = css`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const THUMBNAIL_TIME_STYLE = css`
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9)};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: center;
  color: ${COLORS.midtoneFaded};
`;

const THUMBNAIL_TIME_ACTIVE_STYLE = css`
  color: ${COLORS.ink};
`;

/**
 * Day as well as time. The bucket above may cover a week or a month, so the time alone would not say
 * which day of it a photo belongs to — and telling two photos of one cell apart is the whole job of
 * these captions.
 */
const getPhotoTimeLabel = (photo: ScanPhoto) => photo.addedAt == null ? '—' : formatMonthDayTime(photo.addedAt);

/**
 * One line of the stack. A step when there is a bucket that far away, and an empty slot of the same
 * height when there is not — the ends of a timeline should not move the date the reader is looking at.
 */
const TimeSlot = ({ label, distance, direction, onSelect }: {
  /** What the bucket is called, or nothing when the timeline does not reach that far. */
  label?: string;
  /** How many buckets away, which is the whole of what sets this line apart from its neighbours. */
  distance: 1 | 2;
  direction: 'earlier' | 'later';
  onSelect: () => void;
}) => {
  const fontSize = TIME_SLOT_FONT_SIZE_PX[distance];
  if (!label) return <span css={[TIME_SLOT_STYLE, TIME_SLOT_EMPTY_STYLE]} aria-hidden='true'>&nbsp;</span>;

  return (
    <button
      type='button'
      css={[TIME_SLOT_STYLE, TIME_STEP_STYLE]}
      style={{ fontSize: pxToRem(fontSize) }}
      data-testid={distance === 1 ? `progress-alt-photo-${direction}` : undefined}
      aria-label={`${direction === 'earlier' ? 'Earlier' : 'Later'}: ${label}`}
      onClick={onSelect}
    >
      {
        /*
        One chevron for the single step the arrow keys make, two for the jump past it. Both glyphs are
        drawn in the same box, so every label in the stack starts on the same pixel. The glyphs point
        right unrotated, so a quarter turn either way points them along time.
      */
      }
      <span css={CARET_STYLE} style={{ transform: `rotate(${direction === 'earlier' ? -90 : 90}deg)` }}>
        {distance === 1 ? <ExpandCaret color='currentColor' /> : <DoubleExpandCaret color='currentColor' />}
      </span>
      <span css={TIME_NAV_TEXT_STYLE}>{label}</span>
    </button>
  );
};

const FilmstripThumbnail = ({ photo, active }: { photo: ScanPhoto; active: boolean }) => {
  const { selectPhoto } = useProgressAlt();
  const imageUrl = usePhotoUriForLayoutSize(photo.photoId, PHOTO_VIEW_THUMBNAIL_WIDTH_PX);

  return (
    <button
      type='button'
      css={THUMBNAIL_STYLE}
      data-testid='progress-alt-photo-thumbnail'
      data-progress-alt-active={active ? 'true' : undefined}
      aria-label={`Show photo taken ${getPhotoTimeLabel(photo)}`}
      aria-pressed={active}
      onClick={() => selectPhoto(photo.photoId)}
    >
      <span css={[THUMBNAIL_FRAME_STYLE, active && THUMBNAIL_FRAME_ACTIVE_STYLE]}>
        {imageUrl ? <img css={THUMBNAIL_IMAGE_STYLE} src={imageUrl} alt='' loading='lazy' decoding='async' /> : null}
      </span>
      <span css={[THUMBNAIL_TIME_STYLE, active && THUMBNAIL_TIME_ACTIVE_STYLE]}>
        {getPhotoTimeLabel(photo)}
      </span>
    </button>
  );
};

/**
 * Under the photo: every other photo of the same cell, and the way to the buckets either side of it.
 *
 * One strip, not two. The cell is the unit the grid navigates in, so the strip's job is to show what
 * else was captured in that same scope and stretch — moving to another scope or another stretch is a
 * move of the cell itself, which is what the controls at the left are for.
 */
export const ProgressAltPhotoFilmstrip = ({ photos }: {
  /** The cell's photos. */
  photos: readonly ScanPhoto[];
}) => {
  const { timeline, cursor, photoNeighbors, moveToCell, selectPhoto } = useProgressAlt();
  if (!timeline || !cursor) return null;

  const firstPeriodStart = getFirstPeriodStart(timeline.rows);
  const labelForRow = (rowIndex: number) => getRowLabel(timeline.rows[rowIndex] ?? {}, timeline.period, firstPeriodStart);
  /** The bucket `distance` steps away in one direction, named and ready to go to. */
  const slotFor = (direction: 'earlier' | 'later', distance: 1 | 2) => {
    const target = photoNeighbors[direction][distance - 1];
    return {
      distance,
      direction,
      label: target ? labelForRow(target.rowIndex) : undefined,
      onSelect: () => {
        if (target) moveToCell(target);
      },
    };
  };

  return (
    <div css={ROW_STYLE} data-testid='progress-alt-photo-filmstrip'>
      {/* Read down, a stretch of the timeline: two buckets back, this one, two forward. */}
      <div css={TIME_NAV_STYLE}>
        <TimeSlot {...slotFor('earlier', 2)} />
        <TimeSlot {...slotFor('earlier', 1)} />
        <span
          css={[TIME_SLOT_STYLE, TIME_CURRENT_STYLE]}
          style={{ fontSize: pxToRem(TIME_SLOT_FONT_SIZE_PX[0]) }}
          data-testid='progress-alt-photo-time-label'
        >
          {labelForRow(cursor.rowIndex)}
        </span>
        <TimeSlot {...slotFor('later', 1)} />
        <TimeSlot {...slotFor('later', 2)} />
      </div>

      <div css={STRIP_STYLE}>
        {photos.map(photo => <FilmstripThumbnail key={photo.photoId} photo={photo} active={photo.photoId === cursor.photoId} />)}
      </div>
    </div>
  );
};
